import _ from "lodash";
import { action, autorun, computed, observable, runInAction } from "mobx";
import { observer } from "mobx-react";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import * as React from "react";
import styled from "styled-components";
import { wsSentryReport } from "./wsSentryReport";
import { lp } from "./logPrefix";
import { UserInfo } from "./UserInfo";
import { UserInfoContext } from "./UserInfoContext";
import { WSErrorContainerContext } from "./WSErrorContainerContext";
import { routerStore } from "./WSRouter";

@observer
export class WSErrorContainer extends React.Component<
  {
    errorMessage?: any;
    showDialogOnCrash?: boolean;
    onComponentDidCatch?: (error: any, info: any) => any;
    onDismiss?: () => any;
  },
  { hasError: boolean }
> {
  render() {
    if (this.state.hasError) {
      return (
        <StyledMessage
          className="ws-error-message"
          severity="error"
          content={
            <div
              onClick={() =>
                this.reportError({
                  forceShowDialog: true,
                  alreadyReported: true,
                })
              }
            >
              {!!this.isLoadingChunkError && (
                <div style={{ fontWeight: "bold" }}>
                  {this.context.locStore
                    .id("c2366e204e86")

                    .loc(
                      "[Loading chunk error] Denne typen feil kan av og til rettes med en hard-refresh. (Ctrl+f5 eller CMD+R)"
                    )}
                </div>
              )}
              <div>{this.errorMessage}</div>
              <div>
                EventID: {this.eventId?.slice(this.eventId.length - 12) || ""}
              </div>
            </div>
          }
        />
      );
    }

    return (
      <WSErrorContainerContext.Provider value={this}>
        {this.props.children}
      </WSErrorContainerContext.Provider>
    );
  }

  disposers = [];

  componentWillUnmount() {
    this.disposers.forEach((d) => d());
    this.disposers = [];
  }

  componentDidMount() {
    this.disposers.push(
      autorun(() => {
        routerStore.location;
        this.setState({ hasError: false });
      })
    );
  }

  declare context: UserInfo;
  static contextType = UserInfoContext;

  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  @action addAdditionalErrorMessageInfo = (info: {
    key: string;
    errorMessage: string;
    [x: string]: any;
  }) => {
    _.remove(
      this.additionalErrorMessageInfo,
      (_info) => _info.key === info.key
    );

    this.additionalErrorMessageInfo.push(info);
  };

  @observable additionalErrorMessageInfo: {
    key: string;
    errorMessage: string;
    [x: string]: any;
  }[] = [];

  @computed get onDismiss() {
    return this.props.onDismiss;
  }

  @computed get isLoadingChunkError() {
    try {
      const msg = ((this.error || {}).message + "").toLowerCase();

      return msg.startsWith("loading chunk") || msg.includes(".chunk.js");
    } catch (e) {
      return false;
    }
  }

  static defaultProps = {
    showDialogOnCrash: false,
  };

  @computed get errorMessage() {
    const additionalInfo = this.additionalErrorMessageInfo;

    if (additionalInfo.length > 0)
      return _.uniq(additionalInfo.map((x) => x.errorMessage))
        .filter((x) => !!x)
        .join("\r\n");

    if (this.props.errorMessage !== undefined) return this.props.errorMessage;

    return this.context.locStore.id("00a2acd47c80").loc("Noe gikk galt");
  }

  @observable error = null;
  @observable info = null;
  @observable eventId: string = null;

  @action setError(error, info) {
    runInAction(() => {
      this.error = error;
      this.info = info;
    });

    console.error(lp(), info);

    if (this.props.onComponentDidCatch) {
      this.props.onComponentDidCatch(error, info);
    }

    this.reportError({});
  }

  componentDidCatch(error, info) {
    this.setError(error, info);
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  @action reportError = ({
    forceShowDialog = false,
    alreadyReported = false,
  }) => {
    this.eventId = wsSentryReport({
      simpleMessage: this.errorMessage,
      userInfo: this.context,
      eventId: this.eventId,
      error: this.error,
      additionalErrorMessageInfo: this.additionalErrorMessageInfo,
      info: this.info,
      tag: "WSErrorContainer",
      showDialog: this.props.showDialogOnCrash || forceShowDialog,
      alreadyReported,
    });
  };
}

const StyledMessage = styled(Message)`
  &.ws-error-message {
    cursor: pointer;
    white-space: pre-wrap;
  }
`;
